import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Send } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useBulkSetPayerMethod, type Payer } from '@/api/payers';

// Shown right after an admin configures Office Ally for the first time.
// Every Payer that isn't already on Electronic (and isn't the system
// Self-Pay row) is listed with a checkbox — the admin opts in whichever
// payers actually accept 837P. Default-selects all commercial-looking
// payers so the happy path is a single confirm; they can uncheck edge
// cases they'd rather keep manual.

interface Props {
    payers: Payer[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDone?: () => void;
}

export function OABulkOptInDialog({ payers, open, onOpenChange, onDone }: Props) {
    const mutation = useBulkSetPayerMethod();
    const [selected, setSelected] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        if (open) {
            // Self-Pay (system payer with type='Self-Pay') never makes sense
            // on Electronic — it's an internal marker for private-pay flows.
            // Pre-check the rest so the admin just hits Confirm.
            const defaults = new Set<string>();
            for (const p of payers) {
                if (p.submissionMethod === 'Electronic') continue;
                if (p.type === 'Self-Pay') continue;
                defaults.add(p.id);
            }
            setSelected(defaults);
            mutation.reset();
        }
    }, [open, payers]); // eslint-disable-line react-hooks/exhaustive-deps

    const eligible = payers.filter(
        (p) => p.submissionMethod !== 'Electronic' && p.type !== 'Self-Pay',
    );

    function toggle(id: string, checked: boolean) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    async function onConfirm() {
        if (selected.size === 0) {
            onOpenChange(false);
            return;
        }
        try {
            await mutation.mutateAsync({ ids: Array.from(selected), method: 'Electronic' });
            onDone?.();
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            const data = e.response?.data as { error?: string } | undefined;
            return data?.error ?? 'Failed to update payers';
        }
        return 'Unexpected error';
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),36rem)]">
                <DialogHeader>
                    <DialogTitle>Opt payers into electronic submission?</DialogTitle>
                    <DialogDescription>
                        Office Ally is now configured. Pick the payers that accept 837P —
                        their future claims will go out through the EDI batch. Leave the
                        rest on Manual; you can flip individual payers later.
                    </DialogDescription>
                </DialogHeader>

                {serverError && <Alert variant="danger" className="mb-3">{serverError}</Alert>}

                {eligible.length === 0 ? (
                    <p className="text-sm text-text-muted">
                        No eligible payers — every payer is already on Electronic, or the
                        only remaining one is Self-Pay.
                    </p>
                ) : (
                    <div className="max-h-80 overflow-y-auto border border-border rounded-md divide-y divide-border">
                        {eligible.map((p) => {
                            const isChecked = selected.has(p.id);
                            return (
                                <label
                                    key={p.id}
                                    className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted"
                                >
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={isChecked}
                                        onChange={(e) => toggle(p.id, e.target.checked)}
                                    />
                                    <span className="flex-1">
                                        <span className="font-medium text-text">{p.name}</span>
                                        {p.payerId && (
                                            <span className="ml-2 font-mono text-2xs text-text-subtle">
                                                {p.payerId}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-2xs uppercase tracking-wider text-text-subtle">
                                        {p.type || '—'}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={onConfirm}
                        disabled={mutation.isPending || eligible.length === 0}
                    >
                        <Send className="w-3.5 h-3.5" />
                        {mutation.isPending
                            ? 'Updating…'
                            : selected.size === 0
                                ? 'Skip for now'
                                : `Flip ${selected.size} to Electronic`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
